import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { WkoLoadingSpinner } from "../WkoLoadingSpinner";
import { notifyError } from "../../utils/notifications";
const DISPLAY_TOGGLE_RATIO = 0.1;
function calcPillHeight(maxHeight, value, maxValue) {
    if (maxValue <= 0)
        return 0;
    if (value >= maxValue)
        return maxHeight;
    return Math.sqrt(maxHeight ** 2 * (value / maxValue));
}
export function LevelsStatsPill({ options }) {
    const [loading, setLoading] = useState(true);
    const [l1Avg, setL1Avg] = useState(null);
    const [l2Avg, setL2Avg] = useState(null);
    const [l3Avg, setL3Avg] = useState(null);
    const [l4Avg, setL4Avg] = useState(null);
    const containerRef = useRef(null);
    useEffect(() => {
        if (!options.isReady)
            return;
        setL1Avg(null);
        setL2Avg(null);
        setL3Avg(null);
        setL4Avg(null);
        setLoading(true);
        const params = new URLSearchParams({
            fromMonth: options.startDate.substring(0, 7),
            toMonth: options.endDate.substring(0, 7),
            vesselTypes: options.vesselTypes.join(","),
            fleets: options.fleet.map((f) => f.ID).join(","),
        });
        fetch(`/api/levelsbenchmark/globalstat?${params}`)
            .then((res) => {
            if (res.ok)
                return res.json();
            throw new Error(res.statusText);
        })
            .then((stats) => {
            setL1Avg(Math.round(stats.L1Average * 100) / 100);
            setL2Avg(Math.round(stats.L2Average * 100) / 100);
            setL3Avg(Math.round(stats.L3Average * 100) / 100);
            setL4Avg(Math.round(stats.L4Average * 100) / 100);
        })
            .catch(() => {
            notifyError("An error has occurred when loading level stats. Please try again.");
        })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options.isReady, options.startDate, options.endDate, options.vesselTypes.join(","), options.fleet.map((f) => f.ID).join(",")]);
    useEffect(() => {
        const el = containerRef.current;
        if (!el || l1Avg == null || l1Avg <= 0)
            return;
        const maxHeight = el.clientHeight;
        if (maxHeight <= 0)
            return;
        el.style.setProperty("--l1-ratio-height", `${maxHeight}px`);
        el.style.setProperty("--l2-ratio-height", `${calcPillHeight(maxHeight, l2Avg ?? 0, l1Avg)}px`);
        el.style.setProperty("--l3-ratio-height", `${calcPillHeight(maxHeight, l3Avg ?? 0, l1Avg)}px`);
        el.style.setProperty("--l4-ratio-height", `${calcPillHeight(maxHeight, l4Avg ?? 0, l1Avg)}px`);
    }, [l1Avg, l2Avg, l3Avg, l4Avg]);
    if (loading)
        return _jsx(WkoLoadingSpinner, {});
    return _jsx(LevelsPill, { ref: containerRef, l1: l1Avg, l2: l2Avg, l3: l3Avg, l4: l4Avg });
}
function LevelsPill({ l1, l2, l3, l4, ref }) {
    if (l1 == null)
        return null;
    if (l1 <= 0)
        return _jsx("p", { className: "h2 m-0 px-3", children: "No NC at level 1 or higher in this period" });
    return (_jsxs("div", { ref: ref, className: `globalstats-pill${(l4 / l1) < DISPLAY_TOGGLE_RATIO ? " mb-2" : ""}`, children: [l2 < l1 && (_jsxs("p", { className: "pill-value l1-value", title: "NCs level 1 or higher per vessel per month", children: ["L1+: ", _jsx("span", { children: l1 }), " nc/vessel/month"] })), l3 < l2 && (_jsxs("p", { className: "pill-value l2-value", title: "NCs level 2 or higher per vessel per month", children: ["L2+: ", _jsx("span", { children: l2 }), " nc/vessel/month"] })), l2 > 0 && l4 < l3 && (_jsxs("p", { className: "pill-value l3-value", title: "NCs level 3 or higher per vessel per month", children: ["L3+: ", _jsx("span", { children: l3 }), " nc/vessel/month"] })), l3 > 0 && (_jsxs("p", { className: "pill-value l4-value", title: "NCs level 4 (consecutive NCs) per vessel per month", children: ["L4: ", _jsx("span", { children: l4 }), " nc/vessel/month"] })), _jsxs("div", { className: "pills", children: [_jsx("div", { className: "pill l1-pill shadow" }), _jsx("div", { className: "pill l2-pill shadow" }), _jsx("div", { className: "pill l3-pill shadow" }), _jsx("div", { className: "pill l4-pill shadow" })] })] }));
}
